/** Auth context: session token + user info + permissions. */
import { Box, CircularProgress } from '@mui/material';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { isMfaPending, type LoginResponse } from '@/api/auth';
import * as authApi from '@/api/auth';
import { setUnauthorizedHandler } from '@/api/client';
import { fetchMe, type ConsoleMe } from '@/api/me';
import {
  adoptTokenFromOtherTabs,
  clearToken,
  getToken,
  setToken,
  subscribeTokenChange,
} from '@/api/tokenStorage';

import { loginPathFrom } from './returnTo';

/** /auth/login, optionally carrying the interrupted location so sign-in can
 *  return there. No returnTo when the user was already inside /auth/ — except
 *  on the MCP sign-in handoff, which needs the session it lost. */
function loginPath(): string {
  return loginPathFrom(window.location);
}

interface AuthContextValue {
  isHydrated: boolean;
  user: ConsoleMe['user'] | null;
  permissions: string[];
  groups: ConsoleMe['groups'];
  hasPerm: (codename: string) => boolean;
  login: (email: string, password: string) => Promise<LoginResponse>;
  logout: () => Promise<void>;
  refreshMe: () => Promise<void>;
  /** Used by the MFA/verify flow + finalize: bypass /login/ and set token directly. */
  acceptSession: (token: string, expiresAt: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const [isHydrated, setIsHydrated] = useState(false);
  const [user, setUser] = useState<ConsoleMe['user'] | null>(null);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [groups, setGroups] = useState<ConsoleMe['groups']>([]);

  // Re-route on 401 from the axios interceptor — carrying the interrupted
  // location so sign-in returns there.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      setUser(null);
      setPermissions([]);
      setGroups([]);
      navigate(loginPath(), { replace: true });
    });
  }, [navigate]);

  const refreshMe = useCallback(async () => {
    if (!getToken()) {
      setUser(null);
      setPermissions([]);
      setGroups([]);
      return;
    }
    const me = await fetchMe();
    setUser(me.user);
    setPermissions(me.permissions);
    setGroups(me.groups);
  }, []);

  // Initial hydration. A fresh tab has an empty sessionStorage — before
  // deciding we're logged out, ask living tabs for the session token
  // (≤300 ms, only on genuinely token-less boots).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!getToken()) await adoptTokenFromOtherTabs();
        await refreshMe();
      } catch {
        clearToken();
        if (!cancelled) {
          setUser(null);
          setPermissions([]);
          setGroups([]);
        }
      } finally {
        if (!cancelled) setIsHydrated(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [refreshMe]);

  // Cross-tab session changes: a remote logout takes this tab too (shared
  // session); a remote sign-in while this tab sits logged-out hydrates it.
  // Remote rotations arrive as hasToken=true with a user already set — no-op.
  useEffect(() => {
    return subscribeTokenChange((hasToken) => {
      if (!hasToken) {
        setUser(null);
        setPermissions([]);
        setGroups([]);
        navigate(loginPath(), { replace: true });
        return;
      }
      setUser((current) => {
        if (current === null) void refreshMe().catch(() => {});
        return current;
      });
    });
  }, [navigate, refreshMe]);

  const login = useCallback(async (email: string, password: string): Promise<LoginResponse> => {
    const r = await authApi.login(email, password);
    if (!isMfaPending(r)) {
      setToken(r.token, r.token_expires);
      await refreshMe();
    }
    return r;
  }, [refreshMe]);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {
      // Ignore — we're tearing down regardless.
    }
    clearToken();
    setUser(null);
    setPermissions([]);
    setGroups([]);
    navigate('/auth/login', { replace: true });
  }, [navigate]);

  const acceptSession = useCallback(
    async (token: string, expiresAt: string) => {
      setToken(token, expiresAt);
      await refreshMe();
    },
    [refreshMe],
  );

  const hasPerm = useCallback(
    (codename: string) => permissions.includes(codename),
    [permissions],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      isHydrated,
      user,
      permissions,
      groups,
      hasPerm,
      login,
      logout,
      refreshMe,
      acceptSession,
    }),
    [isHydrated, user, permissions, groups, hasPerm, login, logout, refreshMe, acceptSession],
  );

  if (!isHydrated) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const v = useContext(AuthContext);
  if (!v) {
    throw new Error('useAuth() must be used inside <AuthProvider>');
  }
  return v;
}

/** Non-throwing variant for components that can render without an auth
 *  context (e.g. hooks exercised in page tests that don't mount
 *  AuthProvider). Returns null instead of throwing. */
export function useOptionalAuth(): AuthContextValue | null {
  return useContext(AuthContext);
}
